/**
 * Hybrid Logical Clock (HLC) implementation for Superelectric Ink.
 *
 * Provides causally ordered, monotonically increasing 53-bit safe integer timestamps
 * for Last-Write-Wins (LWW) document tree and settings synchronization across devices.
 *
 * Integer packing:
 *   Timestamp = (physicalMillis * 4096) + (counter & 0xFFF)
 *
 * 41 bits timestamp (~2.19e12 ms, valid past year 2080+) + 12 bits counter (up to 4096 ops/ms)
 * Total: <= 53 bits (strictly <= Number.MAX_SAFE_INTEGER 9,007,199,254,740,991).
 * Safe across JSON serialization, SQLite/D1 integers, browsers, and Workers without precision loss.
 */

const HLC_COUNTER_MASK = 0xFFF // 4095
export const HLC_MULTIPLIER = 4096    // 2^12
/**
 * How far ahead of this device's clock a timestamp may be and still be
 * believed. Further ahead (or past 2^53) it came from a broken clock;
 * adopting it would freeze this clock there and make later edits lose.
 */
const MAX_HLC_LEAD_MS = 24 * 60 * 60 * 1000

/** Whether `hlc` is a usable timestamp: a safe integer, not far in the future. */
export function isPlausibleHlc(hlc: unknown, nowMs = Date.now()): hlc is number {
  return typeof hlc === 'number' && Number.isSafeInteger(hlc) && hlc >= 0
    && hlc <= (nowMs + MAX_HLC_LEAD_MS) * HLC_MULTIPLIER + HLC_COUNTER_MASK
}

export interface HLCParsed {
  millis: number
  counter: number
}

export class HLC {
  private lastPhysical = 0
  private counter = 0

  /**
   * Generates a new 53-bit integer HLC timestamp for a local mutation.
   */
  public now(): number {
    const physical = Date.now()
    if (physical > this.lastPhysical) {
      this.lastPhysical = physical
      this.counter = 0
    } else {
      this.counter = (this.counter + 1) & HLC_COUNTER_MASK
    }
    return (this.lastPhysical * HLC_MULTIPLIER) + this.counter
  }

  /**
   * Updates local clock given a remote timestamp and returns the updated timestamp.
   */
  public receive(remote: number): number {
    if (!isPlausibleHlc(remote)) return this.now()
    const remotePhysical = Math.floor(remote / HLC_MULTIPLIER)
    const remoteCounter = remote % HLC_MULTIPLIER
    const localPhysical = Date.now()

    if (localPhysical > this.lastPhysical && localPhysical > remotePhysical) {
      this.lastPhysical = localPhysical
      this.counter = 0
    } else if (remotePhysical > this.lastPhysical) {
      this.lastPhysical = remotePhysical
      this.counter = (remoteCounter + 1) & HLC_COUNTER_MASK
    } else {
      this.counter = (Math.max(this.counter, remoteCounter) + 1) & HLC_COUNTER_MASK
    }

    return (this.lastPhysical * HLC_MULTIPLIER) + this.counter
  }

  /**
   * Deconstructs a 53-bit packed HLC timestamp into physical milliseconds and logical counter.
   */
  public static parse(hlc: number): HLCParsed {
    return {
      millis: Math.floor(hlc / HLC_MULTIPLIER),
      counter: hlc % HLC_MULTIPLIER,
    }
  }

  /**
   * Formats a packed HLC timestamp into an ISO-like readable string (e.g. 2026-08-23T12:00:00.000Z-0001).
   */
  public static toString(hlc: number): string {
    const { millis, counter } = HLC.parse(hlc)
    return `${new Date(millis).toISOString()}-${counter.toString().padStart(4, '0')}`
  }

  /**
   * Compares two HLC timestamps.
   * Returns negative if a < b, positive if a > b, 0 if equal.
   */
  public static compare(a: number, b: number): number {
    return a - b
  }
}

/** Global local HLC instance for the active client session */
export const localHLC = new HLC()

/**
 * Repairs a stored pair of timestamps (the latest change, and the last one
 * synced) when either came from a broken clock. A synced change resets, so
 * the cloud's copy wins on the next pull; an unsent one is re-stamped so it
 * still goes out. null when both are fine.
 */
export function repairStoredHlcs(hlc: number, syncedHlc: number): { hlc: number; syncedHlc: number } | null {
  if (isPlausibleHlc(hlc) && isPlausibleHlc(syncedHlc)) return null
  return hlc <= syncedHlc ? { hlc: 0, syncedHlc: 0 } : { hlc: localHLC.now(), syncedHlc: 0 }
}
